import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import type { PlatformTypes } from '@/types/gql.generated';

const SELECTION_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Which surface owns the selection. The legacy MultiExportBar ('bar') has no
 * list of its own so it puts the page's cards into checkbox mode; the export
 * sidebar ships its own list and must not. See useCardSelectMode.
 */
export type SelectModeSurface = 'bar' | 'sidebar';

type State = {
  isSelectMode: boolean;
  selectedEventIds: Record<string, boolean>;
  experienceId: string | null;
  /**
   * The destination platform the selection was made for, when the caller
   * knows it. Persisted so a surface reloaded mid-flow (e.g. the OAuth
   * connect round trip) can restore its destination alongside the selection.
   */
  platform: PlatformTypes | null;
  surface: SelectModeSurface;
  savedAt: number | null;
  enterSelectMode: (
    experienceId: string,
    platform?: PlatformTypes,
    surface?: SelectModeSurface
  ) => void;
  exitSelectMode: () => void;
  toggleEvent: (eventId: string) => void;
  setSelectedEvents: (eventIds: string[]) => void;
};

export const useSelectModeStore = createWithEqualityFn<State>()(
  persist(
    (set) => ({
      isSelectMode: false,
      selectedEventIds: {},
      experienceId: null,
      platform: null,
      surface: 'bar',
      savedAt: null,
      enterSelectMode: (
        experienceId: string,
        platform?: PlatformTypes,
        surface: SelectModeSurface = 'bar'
      ) =>
        set({
          isSelectMode: true,
          selectedEventIds: {},
          experienceId,
          platform: platform ?? null,
          surface,
          savedAt: Date.now(),
        }),
      exitSelectMode: () =>
        set({
          isSelectMode: false,
          selectedEventIds: {},
          experienceId: null,
          platform: null,
          surface: 'bar',
          savedAt: null,
        }),
      toggleEvent: (eventId: string) =>
        set((state) => {
          const next = { ...state.selectedEventIds };
          if (next[eventId]) {
            delete next[eventId];
          } else {
            next[eventId] = true;
          }
          return { selectedEventIds: next, savedAt: Date.now() };
        }),
      // Replace the whole selection (e.g. select-all / deselect-all). Used by the
      // Export sidebar, which shows checkboxes with everything selected by default.
      setSelectedEvents: (eventIds: string[]) =>
        set({
          selectedEventIds: Object.fromEntries(
            eventIds.map((id) => [id, true])
          ),
          savedAt: Date.now(),
        }),
    }),
    {
      name: '_ah_selected_events',
      partialize: (state) => ({
        isSelectMode: state.isSelectMode,
        selectedEventIds: state.selectedEventIds,
        experienceId: state.experienceId,
        platform: state.platform,
        surface: state.surface,
        savedAt: state.savedAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (
          state?.savedAt &&
          Date.now() - state.savedAt > SELECTION_EXPIRY_MS
        ) {
          state.isSelectMode = false;
          state.selectedEventIds = {};
          state.experienceId = null;
          state.platform = null;
          state.surface = 'bar';
          state.savedAt = null;
        }
      },
    }
  ),
  shallow
);

export const useIsEventSelected = (eventId: string) =>
  useSelectModeStore((state) => !!state.selectedEventIds[eventId]);

export const useSelectedCount = () =>
  useSelectModeStore((state) => Object.keys(state.selectedEventIds).length);

export const useSelectedEventIds = () =>
  useSelectModeStore((state) => Object.keys(state.selectedEventIds));

export const useSelectMode = () =>
  useSelectModeStore((state) => ({
    isSelectMode: state.isSelectMode,
    experienceId: state.experienceId,
    enterSelectMode: state.enterSelectMode,
    exitSelectMode: state.exitSelectMode,
  }));
